import config from '@config';
import RoomType from '@business/rooms/RoomType';
import SpecialOffer from '@business/offers/SpecialOffer';
import RestaurantBox from '@business/restaurant/RestaurantBox';
import { EventRoom } from '@business/events/EventRoom';
import SpaInfoBox from '@business/spa/SpaInfoBox';

export enum UrlType
{
    Rooms,
    RoomType,
    SpecialOffers,
    SpecialOffer,
    Restaurant,
    RestaurantBox,
    Events,
    EventRoom,
    Weddings,
    Spa,
    SpaInfoBox,
    Contact,
    PrivacyPolicy,
    TermsAndConditions,
    SpaTermsAndConditions,
    CookiePolicy,
    SpaVoucher,
    EmailOptIn,
    EmailOptInConfirmation,
}

export const UrlTypeSlugMapping: { [key: string]: LanguageSlugMapping } = {
    [UrlType.Rooms]: {
        ro: 'camere',
        hu: 'szobak',
        en: 'rooms',
        de: 'zimmer',
    },
    [UrlType.SpecialOffers]: {
        ro: 'oferte-speciale',
        hu: 'promocios-ajanlatok',
        en: 'special-offers',
        de: 'sonderangebote',
    },
    [UrlType.Restaurant]: {
        ro: 'restaurant',
        hu: 'etterem',
        en: 'restaurant',
        de: 'restaurant',
    },
    [UrlType.Events]: {
        ro: 'evenimente',
        hu: 'esemenyek',
        en: 'events',
        de: 'veranstaltungen',
    },
    [UrlType.Weddings]: {
        ro: 'nunti',
        hu: 'eskuvok',
        en: 'weddings',
        de: 'hochzeiten',
    },
    [UrlType.Spa]: {
        ro: 'spa',
        hu: 'spa',
        en: 'spa',
        de: 'spa',
    },
    [UrlType.Contact]: {
        ro: 'contact',
        hu: 'kapcsolat',
        en: 'contact',
        de: 'kontakt',
    },
    [UrlType.PrivacyPolicy]: {
        ro: 'politica-de-confidentialitate',
        hu: 'adatvedelem-tajekoztato',
        en: 'privacy-policy',
        de: 'datenschutz',
    },
    [UrlType.TermsAndConditions]: {
        ro: 'termeni-si-conditii',
        hu: 'felhasznalasi-feltetelek',
        en: 'terms-and-conditions',
        de: 'bedingungen-und-bestimmungen',
    },
    [UrlType.SpaTermsAndConditions]: {
        ro: 'termen-si-conditii-spa',
        hu: 'felhasznalasi-feltetelek-spa',
        en: 'terms-and-conditions-spa',
        de: 'bedingungen-und-bestimmungen-spa',
    },
    [UrlType.CookiePolicy]: {
        ro: 'politica-de-cookie-uri',
        hu: 'cookie-tajekoztato',
        en: 'cookie-policy',
        de: 'cookie-richtlinie',
    },
    [UrlType.EmailOptIn]: {
        ro: 'email-opt-in',
        hu: 'email-opt-in',
        en: 'email-opt-in',
        de: 'email-opt-in',
    },
};

export class UrlHelper
{
    /** The singleton instance. */
    protected static _instance?: UrlHelper;
    /** The base URL of the application. */
    websiteUrl!: string;

    protected constructor()
    {
        this.websiteUrl = config.appUrl;
    }

    /**
     * Returns the singleton instance.
     */
    static get instance()
    {
        if (this._instance === undefined) 
        {
            this._instance = this.createInstance();
        }

        return this._instance;
    }

    /**
     * Create the singleton instance. Override this method to create a custom instance.
     */
    protected static createInstance(): UrlHelper
    {
        return new UrlHelper();
    }

    /**
     * Returns the base URL of the application.
     * 
     * @param relative Whether to return a relative URL (eg: '/rooms') or an absolute URL (eg: 'https://www.hotelfenyo.ro/rooms').
     */
    getBaseUrl(relative = true)
    {
        return relative ? '/' : this.websiteUrl;
    }

    /**
     * Returns the URL for a given language and slug.
     * 
     * @param language The language of the URL.
     * @param slug The slug of the URL. Can be any string actually. Eg: 'rooms', 'rooms/single', etc.
     */
    getUrlForLanguageAndSlug(language: string, slug: string)
    {
        return this.getBaseUrl() + language + '/' + slug;
    }

    /**
     * Returns the URL for the rooms page for a given language.
     */
    getRoomsUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.Rooms];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for the room type page for a given language.
     */
    getRoomTypeUrl(language: string, roomType: RoomType)
    {
        let roomTypeTranslation = roomType.getTranslation(language);
        let roomsPageSlug = UrlTypeSlugMapping[UrlType.Rooms][language];
        let roomTypeSlug = roomTypeTranslation?.slug ?? '';

        return this.getUrlForLanguageAndSlug(language, `${roomsPageSlug}/${roomTypeSlug}`);
    }

    /**
     * Returns the URL for the special offers page for a given language.
     */
    getSpecialOffersUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.SpecialOffers];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for a specific special offer for a given language.
     */
    getSpecialOfferUrl(language: string, specialOffer: SpecialOffer)
    {
        let specialOffersPageSlug = UrlTypeSlugMapping[UrlType.SpecialOffers][language];
        let specialOfferTranslation = specialOffer.getTranslation(language);
        let specialOfferSlug = specialOfferTranslation?.slug ?? '';
        
        return this.getUrlForLanguageAndSlug(language, `${specialOffersPageSlug}/${specialOfferSlug}`);
    }

    /**
     * Returns the URL for the restaurant page for a given language.
     */
    getRestaurantUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.Restaurant];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for a specific restaurant box for a given language.
     */
    getRestaurantBoxUrl(language: string, restaurantBox: RestaurantBox)
    {
        let restaurantPageSlug = UrlTypeSlugMapping[UrlType.Restaurant][language];
        let restaurantBoxTranslation = restaurantBox.getTranslation(language);
        let restaurantBoxSlug = restaurantBoxTranslation?.slug ?? '';
        
        return this.getUrlForLanguageAndSlug(language, `${restaurantPageSlug}/${restaurantBoxSlug}`);
    }

    /**
     * Returns the URL for the events page for a given language.
     */
    getEventsUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.Events];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for a specific event room for a given language.
     */
    getEventRoomUrl(language: string, eventRoom: EventRoom)
    {
        let eventsPageSlug = UrlTypeSlugMapping[UrlType.Events][language];
        let eventRoomTranslation = eventRoom.getTranslation(language);
        let eventRoomSlug = eventRoomTranslation?.slug ?? '';
        
        return this.getUrlForLanguageAndSlug(language, `${eventsPageSlug}/${eventRoomSlug}`);
    }

    /**
     * Returns the URL for the weddings page for a given language.
     */
    getWeddingsUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.Weddings];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for the spa page for a given language.
     */
    getSpaUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.Spa];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for a specific spa info box for a given language.
     */
    getSpaInfoBoxUrl(language: string, spaInfoBox: SpaInfoBox)
    {
        let spaPageSlug = UrlTypeSlugMapping[UrlType.Spa][language];
        let spaInfoBoxTranslation = spaInfoBox.getTranslation(language);
        let spaInfoBoxSlug = spaInfoBoxTranslation?.slug ?? '';
        
        return this.getUrlForLanguageAndSlug(language, `${spaPageSlug}/${spaInfoBoxSlug}`);
    }

    /**
     * Returns the URL for the contact page for a given language.
     */
    getContactUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.Contact];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for the privacy policy page for a given language.
     */
    getPrivacyPolicyUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.PrivacyPolicy];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for the terms and conditions page for a given language.
     */
    getTermsAndConditionsUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.TermsAndConditions];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for the SPA terms and conditions page for a given language.
     */
    getSpaTermsAndConditionsUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.SpaTermsAndConditions];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for the cookie policy page for a given language.
     */
    getCookiePolicyUrl(language: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.CookiePolicy];
        
        return this.getUrlForLanguageAndSlug(language, mapping[language]);
    }

    /**
     * Returns the URL for an email opt-in form for a given language and form ID.
     */
    getEmailOptInUrl(language: string, formId: number)
    {
        let mapping = UrlTypeSlugMapping[UrlType.EmailOptIn];
        
        return this.getUrlForLanguageAndSlug(language, `${mapping[language]}/${formId}`);
    }

    /**
     * Returns the URL for email opt-in confirmation with token.
     */
    getEmailOptInConfirmationUrl(language: string, confirmationToken: string)
    {
        let mapping = UrlTypeSlugMapping[UrlType.EmailOptIn];
        
        return this.getUrlForLanguageAndSlug(language, `${mapping[language]}/confirmation/${confirmationToken}`);
    }

    /**
     * Get the URL for the spa voucher page (keeping the old URL, legacy mode until the new voucher page is ready)
     */
    getSpaVoucherUrl(language: string)
    {
        let mapping: { [key: string]: string } = {
            ro: 'https://voucher-legacy.hotelfenyo.ro/js/www/?view=voucher-category-view&language=ro',
            hu: 'https://voucher-legacy.hotelfenyo.ro/js/www/?view=voucher-category-view&language=hu',
            en: 'https://voucher-legacy.hotelfenyo.ro/js/www/?view=voucher-category-view&language=ro',
            de: 'https://voucher-legacy.hotelfenyo.ro/js/www/?view=voucher-category-view&language=ro',
        };
        
        return mapping[language];
    }

    /**
     * Returns the translated URL for a detail page (like room type, special offer, etc.)
     * 
     * @param urlType The type of URL (must be a detail page type)
     * @param currentLanguage The current language code
     * @param targetLanguage The target language code to translate to
     * @param pathSegments The current path segments
     * @returns The translated URL path
     */
    async getTranslatedUrlForDetailPage(urlType: UrlType, currentLanguage: string, targetLanguage: string, pathSegments: string[]): Promise<string>
    {
        // Default fallback - just replace the language segment
        let newPath = '/' + targetLanguage + '/' + pathSegments.slice(1).join('/');
        
        // Extract the entity slug (usually the second segment after language)
        if (pathSegments.length < 2) return newPath;
        
        const parentSlug = pathSegments[1];
        const entitySlug = pathSegments.length > 2 ? pathSegments[2] : '';
        
        try
        {
            switch (urlType)
            {
                case UrlType.RoomType:
                    if (entitySlug)
                    {
                        const roomType = await RoomType.getVeryLightForNavigation(currentLanguage, entitySlug);
                        if (roomType)
                        {
                            newPath = this.getRoomTypeUrl(targetLanguage, roomType);
                        }
                    }
                    break;
                    
                case UrlType.SpecialOffer:
                    if (entitySlug)
                    {
                        const specialOffer = await SpecialOffer.getVeryLightForNavigation(currentLanguage, entitySlug);
                        if (specialOffer)
                        {
                            newPath = this.getSpecialOfferUrl(targetLanguage, specialOffer);
                        }
                    }
                    break;
                    
                case UrlType.RestaurantBox:
                    if (entitySlug)
                    {
                        const restaurantBox = await RestaurantBox.getVeryLightForNavigation(currentLanguage, entitySlug);
                        if (restaurantBox)
                        {
                            newPath = this.getRestaurantBoxUrl(targetLanguage, restaurantBox);
                        }
                    }
                    break;
                    
                case UrlType.EventRoom:
                    if (entitySlug)
                    {
                        const eventRoom = await EventRoom.getVeryLightForNavigation(currentLanguage, entitySlug);
                        if (eventRoom)
                        {
                            newPath = this.getEventRoomUrl(targetLanguage, eventRoom);
                        }
                    }
                    break;
                    
                case UrlType.SpaInfoBox:
                    if (entitySlug)
                    {
                        const spaInfoBox = await SpaInfoBox.getVeryLightForNavigation(currentLanguage, entitySlug);
                        if (spaInfoBox)
                        {
                            newPath = this.getSpaInfoBoxUrl(targetLanguage, spaInfoBox);
                        }
                    }
                    break;
            }
        }
        catch (error)
        {
            console.error('Error translating detail page URL:', error);
            // Return fallback path on error
        }
        
        return newPath;
    }

    /**
     * Returns the URL type for a given language and slug.
     * 
     * @param language The language of the URL.
     * @param slug The slug of the URL. Can be any string actually. Eg: 'rooms', 'rooms/single', etc.
     * @returns
     */
    static getUrlTypeForLanguageAndSlug(language: string, slug: string): UrlType | null
    {
        // Special handling for room type URLs (e.g., 'rooms/single')
        let roomsPageSlug = UrlTypeSlugMapping[UrlType.Rooms][language];
        if (roomsPageSlug && slug.startsWith(roomsPageSlug + '/'))
        {
            // If there's content after the rooms slug + slash, it's a room type URL
            let remainingSlug = slug.substring(roomsPageSlug.length + 1);
            if (remainingSlug.length > 0)
            {
                return UrlType.RoomType;
            }
        }

        // Special handling for special offer URLs (e.g., 'special-offers/summer-package')
        let specialOffersPageSlug = UrlTypeSlugMapping[UrlType.SpecialOffers][language];
        if (specialOffersPageSlug && slug.startsWith(specialOffersPageSlug + '/'))
        {
            // If there's content after the special offers slug + slash, it's a special offer URL
            let remainingSlug = slug.substring(specialOffersPageSlug.length + 1);
            if (remainingSlug.length > 0)
            {
                return UrlType.SpecialOffer;
            }
        }

        // Special handling for event room URLs (e.g., 'events/conference-room')
        let eventsPageSlug = UrlTypeSlugMapping[UrlType.Events][language];
        if (eventsPageSlug && slug.startsWith(eventsPageSlug + '/'))
        {
            // If there's content after the events slug + slash, it's an event room URL
            let remainingSlug = slug.substring(eventsPageSlug.length + 1);
            if (remainingSlug.length > 0)
            {
                return UrlType.EventRoom;
            }
        }

        // Special handling for restaurant box URLs (e.g., 'restaurant/menu-name')
        let restaurantPageSlug = UrlTypeSlugMapping[UrlType.Restaurant][language];
        if (restaurantPageSlug && slug.startsWith(restaurantPageSlug + '/'))
        {
            // If there's content after the restaurant slug + slash, it's a restaurant box URL
            let remainingSlug = slug.substring(restaurantPageSlug.length + 1);
            if (remainingSlug.length > 0)
            {
                return UrlType.RestaurantBox;
            }
        }

        // Special handling for spa info box URLs (e.g., 'spa/spa-info-box-slug')
        let spaPageSlug = UrlTypeSlugMapping[UrlType.Spa][language];
        if (spaPageSlug && slug.startsWith(spaPageSlug + '/'))
        {
            // If there's content after the spa slug + slash, it's a spa info box URL
            let remainingSlug = slug.substring(spaPageSlug.length + 1);
            if (remainingSlug.length > 0)
            {
                return UrlType.SpaInfoBox;
            }
        }

        // Special handling for email opt-in URLs (e.g., 'email-opt-in/123' or 'email-opt-in/confirmation/token')
        let emailOptInPageSlug = UrlTypeSlugMapping[UrlType.EmailOptIn][language];
        if (emailOptInPageSlug && slug.startsWith(emailOptInPageSlug + '/'))
        {
            let remainingSlug = slug.substring(emailOptInPageSlug.length + 1);
            if (remainingSlug.length > 0)
            {
                // Check if it's a confirmation URL
                if (remainingSlug.startsWith('confirmation/'))
                {
                    return UrlType.EmailOptInConfirmation;
                }
                // Otherwise it's a regular email opt-in form URL with form ID
                return UrlType.EmailOptIn;
            }
        }

        for (const urlTypeKey in UrlTypeSlugMapping)
        {
            const urlType = parseInt(urlTypeKey) as UrlType;
            const languageMapping = UrlTypeSlugMapping[urlType];
            const mappedSlug = languageMapping[language];
            
            if (mappedSlug && (slug === mappedSlug || slug.startsWith(mappedSlug + '/')))
            {
                return urlType;
            }
        }
        
        return null;
    }
}

export type LanguageSlugMapping = { [key: string]: string };

export default UrlHelper;